import { Button, Card, Modal, ThemeToggle, useToast } from "@adafia/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createResume, deleteResume, getResumes } from "../api/resume";

export function ResumeList() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showToast } = useToast();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [resumeToDelete, setResumeToDelete] = useState<string | null>(null);
	const [newResumeName, setNewResumeName] = useState("");
	const [newResumeSlug, setNewResumeSlug] = useState("");
	const [duplicatingResume, setDuplicatingResume] = useState<string | null>(
		null,
	);

	const { data: resumes, isLoading } = useQuery({
		queryKey: ["resumes"],
		queryFn: getResumes,
	});

	const createMutation = useMutation({
		mutationFn: createResume,
		onSuccess: (newResume) => {
			queryClient.invalidateQueries({ queryKey: ["resumes"] });
			showToast("Resume created successfully", "success");
			setIsModalOpen(false);
			setNewResumeName("");
			setNewResumeSlug("");
			setDuplicatingResume(null);
			navigate(`/${newResume.slug}`);
		},
		onError: () => {
			showToast("Failed to create resume", "error");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteResume,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["resumes"] });
			showToast("Resume deleted", "success");
			setIsDeleteModalOpen(false);
			setResumeToDelete(null);
		},
		onError: () => {
			showToast("Failed to delete resume", "error");
		},
	});

	if (isLoading) return <div className="loading">Loading resumes...</div>;

	const handleCreateClick = () => {
		setDuplicatingResume(null);
		setNewResumeName("");
		setNewResumeSlug("");
		setIsModalOpen(true);
	};

	const handleDuplicateClick = (e: React.MouseEvent, id: string) => {
		e.stopPropagation();
		const resume = resumes?.find((r) => r.id === id);
		if (resume) {
			setDuplicatingResume(id);
			setNewResumeName(`${resume.name} (Copy)`);
			setNewResumeSlug(`${resume.slug}-copy`);
			setIsModalOpen(true);
		}
	};

	const handleDeleteClick = (e: React.MouseEvent, id: string) => {
		e.stopPropagation();
		setResumeToDelete(id);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = () => {
		if (resumeToDelete) {
			deleteMutation.mutate(resumeToDelete);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!newResumeName || !newResumeSlug) return;

		if (duplicatingResume) {
			const baseResume = resumes?.find((r) => r.id === duplicatingResume);
			if (baseResume) {
				const {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					id: _id,
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					createdAt: _ca,
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					updatedAt: _ua,
					...rest
				} = baseResume;
				createMutation.mutate({
					...rest,
					name: newResumeName,
					slug: newResumeSlug,
				});
				return;
			}
		}

		// Create new empty resume
		createMutation.mutate({
			name: newResumeName,
			slug: newResumeSlug,
			isPublished: false,
			personal: {
				name: "",
				title: "",
				email: "",
				phone: "",
				location: "",
				linkedin: "",
				summary: "",
			},
			experience: [],
			education: [],
			projects: [],
			certifications: [],
			skills: [],
		});
	};

	return (
		<div className="resume-list-container">
			<header className="list-header">
				<div style={{ position: "absolute", top: "1rem", right: "1rem" }}>
					<ThemeToggle />
				</div>
				<h1>My Resumes</h1>
				<p>Create and manage multiple resumes for different roles</p>
			</header>

			<div className="resume-grid">
				<Card
					className="resume-list-card resume-add-card"
					onClick={handleCreateClick}
				>
					<div className="resume-add-content">
						<Plus size={32} />
						<span>Create New Resume</span>
					</div>
				</Card>

				{resumes?.map((resume) => (
					<Card
						key={resume.id}
						className="resume-list-card"
						onClick={() => navigate(`/${resume.slug}`)}
					>
						<div className="resume-card-actions">
							<button
								type="button"
								className="action-btn"
								title="Duplicate"
								onClick={(e) => handleDuplicateClick(e, resume.id as string)}
							>
								<Copy size={14} />
							</button>
							<button
								type="button"
								className="action-btn delete"
								title="Delete"
								onClick={(e) => handleDeleteClick(e, resume.id as string)}
							>
								{" "}
								<Trash2 size={14} />
							</button>
						</div>
						<div className="resume-card-content">
							<div
								style={{ display: "flex", alignItems: "center", gap: "10px" }}
							>
								<h3 className="resume-card-title">{resume.name}</h3>
								{resume.isPublished && (
									<span
										style={{
											fontSize: "0.65rem",
											backgroundColor: "var(--color-accent-bg)",
											color: "var(--color-accent)",
											padding: "2px 6px",
											borderRadius: "4px",
											fontWeight: "600",
										}}
									>
										Public
									</span>
								)}
							</div>
							<p className="resume-card-subtitle">
								{resume.personal.title || "No Title"}
							</p>
							<div className="resume-card-footer">
								<span className="slug-tag">/{resume.slug}</span>
								<ExternalLink size={16} />
							</div>
						</div>
					</Card>
				))}
			</div>

			<Modal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title={duplicatingResume ? "Duplicate Resume" : "Create New Resume"}
			>
				<form onSubmit={handleSubmit} className="modal-form">
					<div className="form-group">
						<label htmlFor="name" className="form-label">
							Resume Name (e.g., "Frontend Role")
						</label>
						<input
							id="name"
							className="form-control"
							value={newResumeName}
							onChange={(e) => {
								setNewResumeName(e.target.value);
								if (!duplicatingResume) {
									setNewResumeSlug(
										e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
									);
								}
							}}
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="slug" className="form-label">
							URL Slug
						</label>
						<input
							id="slug"
							className="form-control"
							value={newResumeSlug}
							onChange={(e) => setNewResumeSlug(e.target.value)}
							required
						/>
					</div>
					<div
						style={{
							marginTop: "2rem",
							display: "flex",
							justifyContent: "flex-end",
							gap: "1rem",
						}}
					>
						<Button variant="outline" onClick={() => setIsModalOpen(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={createMutation.isPending}>
							{createMutation.isPending
								? "Creating..."
								: duplicatingResume
									? "Duplicate"
									: "Create"}
						</Button>
					</div>
				</form>
			</Modal>

			<Modal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				title="Delete Resume"
			>
				<div className="modal-body-confirm">
					<p>
						Are you sure you want to delete this resume? This action cannot be
						undone.
					</p>
					<div
						style={{
							marginTop: "2rem",
							display: "flex",
							justifyContent: "flex-end",
							gap: "1rem",
						}}
					>
						<Button
							variant="outline"
							onClick={() => setIsDeleteModalOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={confirmDelete}
							disabled={deleteMutation.isPending}
							style={{ backgroundColor: "#ef4444", borderColor: "#ef4444" }}
						>
							{deleteMutation.isPending ? "Deleting..." : "Delete Permanently"}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
}
